import { IAddItemBase, IProductPricesDetails } from "@interfaces";

/**
 * A guest's cart additions, kept so they can be replayed onto the account made
 * at sign-up.
 *
 * The backend's own hand-over, `PUT /cart/convert/session/{id}`, answers 200 with
 * an empty payload and leaves the account cart empty, so a guest who added a
 * product before signing up loses it. This is the fallback: each add is recorded
 * here as the exact payload that created it, and anything still pending after the
 * convert has run is posted again against the new account.
 *
 * Session storage rather than local: the whole journey happens in one tab, and a
 * stash that outlived the tab would replay stale products into some later
 * sign-up.
 */
const STASH_KEY = "guestCartPendingItems";

export interface IStashedCartItem {
    /** Identifies the entry so a successful replay can drop just that one. */
    id: string;
    item: IAddItemBase;
    /** Cleared once the item is known to be on the account. */
    pending: boolean;
}

const read = (): IStashedCartItem[] => {
    try {
        const raw = sessionStorage.getItem(STASH_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        // Unreadable or unparseable storage is treated as an empty stash: this is
        // a fallback for a lost cart, never a reason to fail the journey.
        return [];
    }
};

const write = (items: IStashedCartItem[]) => {
    try {
        sessionStorage.setItem(STASH_KEY, JSON.stringify(items));
    } catch {
        // Private-mode quota failures are not worth failing an add over.
    }
};

/**
 * The identity of a cart line, used to tell a replayed item from one the backend
 * already moved across. Product, fare and travel date together are what make two
 * rows the same booking; quantity is deliberately excluded, because a partially
 * converted row should not be added a second time.
 */
export const stashItemKey = (item: IAddItemBase): string => {
    const fares = (item.productPricesDetails ?? [])
        .flatMap((detail: IProductPricesDetails) =>
            (detail.quantityDetails ?? []).map(
                (q) => `${detail.productPricesDetailsId}:${q.bookingDate}`,
            ),
        )
        .sort()
        .join("|");
    return `${item.tdmsProductId}#${fares}`;
};

export const stashGuestCartItem = (item: IAddItemBase) => {
    const items = read();
    const key = stashItemKey(item);
    // Re-adding the same booking replaces its entry rather than queueing a second
    // replay; the guest cart itself already holds the combined quantity.
    write([
        ...items.filter((entry) => stashItemKey(entry.item) !== key),
        { id: key, item, pending: true },
    ]);
};

export const readPendingStashedItems = (): IStashedCartItem[] =>
    read().filter((entry) => entry.pending);

export const markStashedItemAdded = (id: string) => {
    write(read().map((entry) => (entry.id === id ? { ...entry, pending: false } : entry)));
};

export const clearGuestCartStash = () => {
    try {
        sessionStorage.removeItem(STASH_KEY);
    } catch {
        // A stash that cannot be cleared is re-read against a cart that already
        // holds the items, and every entry is then skipped as a duplicate.
    }
};

/**
 * Drop anything the guest cart no longer holds.
 *
 * The stash lives as long as the tab, so without this a product the visitor
 * added and then removed would be posted back onto their account at sign-up —
 * and, being in the cart again, would collide with a later Book Now on the same
 * product. Reconciled against the cart rather than hooked into each delete, so
 * it stays right however an item leaves.
 */
export const reconcileStashWithCart = (
    cartRows: {
        tdms_product_id?: number | string;
        product_price_details_id?: number | string;
        booking_date?: string;
    }[],
) => {
    const stashed = read();
    if (stashed.length === 0) return;

    const present = new Set(
        cartRows.map(
            (row) => `${row.tdms_product_id}#${row.product_price_details_id}:${row.booking_date}`,
        ),
    );
    write(
        stashed.filter((entry) =>
            (entry.item.productPricesDetails ?? []).some((detail) =>
                (detail.quantityDetails ?? []).some((q) =>
                    present.has(
                        `${entry.item.tdmsProductId}#${detail.productPricesDetailsId}:${q.bookingDate}`,
                    ),
                ),
            ),
        ),
    );
};

/**
 * Which stashed items still need posting to the account, given what the account
 * cart already holds. Anything the backend's convert did move across is skipped,
 * so the fallback stays correct once that endpoint is fixed.
 */
export const selectItemsToReplay = (
    stashed: IStashedCartItem[],
    accountRows: {
        tdms_product_id?: number | string;
        product_price_details_id?: number | string;
        booking_date?: string;
    }[],
): IStashedCartItem[] => {
    const present = new Set(
        accountRows.map(
            (row) => `${row.tdms_product_id}#${row.product_price_details_id}:${row.booking_date}`,
        ),
    );
    return stashed.filter(
        (entry) =>
            entry.pending &&
            !(entry.item.productPricesDetails ?? []).some((detail) =>
                (detail.quantityDetails ?? []).some((q) =>
                    present.has(
                        `${entry.item.tdmsProductId}#${detail.productPricesDetailsId}:${q.bookingDate}`,
                    ),
                ),
            ),
    );
};
